import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ATTRAPPE, hole, sende } from "./klient";
import type { Bandeintrag, Verteilperson } from "./faelle";
import type { Einsatz } from "./einteilen";
import { anlegeRumpf, type Ortsaufnahmeentwurf } from "./ortsaufnahmeregeln";
import * as R from "./routen";

/* ══════════════════════════════════════════════════════════════════
   Das Fenster „Ortsaufnahme anlegen" — die Abrufe (08.10.2026, Paket
   ortsaufnahme). Die Regeln stehen rein in `daten/ortsaufnahmeregeln.ts`.
   ══════════════════════════════════════════════════════════════════ */

/** `GET /aufnahmen/lage` — wer in Frage kommt, und was die Leistung verlangt. */
export type Aufnahmelage = {
  von: string;
  bis: string;
  leute: Verteilperson[];
  band: Bandeintrag[];
  einsaetze: Einsatz[];
  verlangt: string[];
  qualifikationen: { code: string; label: string }[];
};

export const useAufnahmeLage = (von: string, bis: string, leistung: string, an = true) =>
  useQuery({
    queryKey: ["aufnahmelage", von, bis, leistung],
    enabled: an,
    queryFn: () => (ATTRAPPE
      ? Promise.resolve<Aufnahmelage>({ von, bis, leute: [], band: [], einsaetze: [], verlangt: [], qualifikationen: [] })
      : hole<Aufnahmelage>(`/aufnahmen/lage?von=${von}&bis=${bis}${leistung ? `&leistungsart=${leistung}` : ""}`)),
  });

/** Was `POST /aufnahmen` zurückgibt — hier gebraucht: die Kennung (für „gleich aufnehmen"). */
export type AngelegteAufnahme = { id: string; vorgang: string | null };

/**
 * `POST /aufnahmen` mit dem Rumpf aus `anlegeRumpf`. Danach ist frisch, was
 * die Aufnahme zeigt: die Liste, das Telefon, Einteilen, der Start und —
 * bei einer Bestellung — der Vorgang, an dem sie hängt.
 */
export function useOrtsaufnahmeAnlegen() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: async ({ entwurf, selbst, zuordnenAn }: {
      entwurf: Ortsaufnahmeentwurf; selbst?: string | null;
      /** Aus dem Angebot ohne wartende Zeile: danach diesem Vorgang zuordnen (Paket aufnahmen2). */
      zuordnenAn?: string | undefined;
    }): Promise<AngelegteAufnahme> => {
      if (ATTRAPPE) throw new Error("Im Attrappenbetrieb gibt es keine Aufnahme.");
      /* Die Kennungen von Kunde und Ort aus der Akte — derselbe Weg wie
         `aufnahmeSpontan` (`daten/ortsbegehung.ts`): Im Browser trägt der
         Kunde nur seine Nummer. Bei einer Bestellung stehen beide an der Position. */
      let ids = { kundeId: "", ortId: "" };
      if (!entwurf.positionId) {
        const kunde = await hole<Record<string, unknown>>(`/kunden/${encodeURIComponent(entwurf.kundeNr)}`);
        const orte = Array.isArray(kunde["orte"]) ? (kunde["orte"] as Record<string, unknown>[]) : [];
        const ort = entwurf.ort ? orte.find((o) => String(o["label"] ?? "") === entwurf.ort) : undefined;
        ids = { kundeId: String(kunde["id"] ?? ""), ortId: ort ? String(ort["id"] ?? "") : "" };
      }
      const roh = await sende<Record<string, unknown>>("/aufnahmen", anlegeRumpf(entwurf, ids, selbst ?? null));
      const id = String(roh["id"] ?? "");
      /* **Aus dem Angebot angelegt, danach verknüpft** (Paket aufnahmen2). Eine
         Bestellung hängt schon an ihrer Zeile; eine Aufnahme ohne wartende
         Zeile wird dem Angebot jetzt zugeordnet — derselbe Weg wie
         „Ortsaufnahme zuordnen". */
      if (zuordnenAn && !entwurf.positionId && id) {
        const z = await R.aufnahmeZuordnen(id, zuordnenAn);
        return { id, vorgang: z.vorgang };
      }
      return { id, vorgang: typeof roh["vorgang"] === "string" ? roh["vorgang"] : null };
    },
    onSuccess: () => {
      for (const schluessel of [["aufnahmen"], ["aufnahmenAmVorgang"], ["begehungen"], ["einteilen"], ["start"], ["handy"],
        ["vorgang"], ["positionen"], ["planung"], ["angebote"]]) {
        void k.invalidateQueries({ queryKey: schluessel });
      }
    },
  });
}

/* ══════════════════════════════════════════════════════════════════
   Eine Ortsaufnahme im Angebot verwenden (Paket aufnahmen2, 08.10.2026).
   Zuordnen und Lösen gehen über die API (`dienste/aufnahmezuordnung.ts`);
   danach ist frisch, was die Verknüpfung zeigt: die Liste, die Akte der
   Aufnahme, das Angebot mit seinen Positionen, Einteilen und der Start.
   ══════════════════════════════════════════════════════════════════ */

function zuordnungFrisch(k: ReturnType<typeof useQueryClient>, id: string): void {
  for (const schluessel of [["aufnahmen"], ["aufnahmenAmVorgang"], ["aufnahmenZuordenbar"], ["angeboteFuerAufnahme"],
    ["begehung", id], ["begehungen"], ["vorgang"], ["positionen"], ["angebote"], ["einteilen"], ["start"], ["handy"]]) {
    void k.invalidateQueries({ queryKey: schluessel });
  }
}

/** Die Ortsaufnahmen an einem Vorgang — die Karte „Ortsaufnahme" im Angebot. */
export const useAufnahmenAmVorgang = (nr: string, an = true) =>
  useQuery({ queryKey: ["aufnahmenAmVorgang", nr], enabled: an && Boolean(nr), queryFn: () => R.aufnahmenAmVorgang(nr) });

/** Was sich diesem Angebot zuordnen lässt — erst gefragt, wenn das Fenster offen ist. */
export const useAufnahmenZuordenbar = (nr: string, an: boolean) =>
  useQuery({ queryKey: ["aufnahmenZuordenbar", nr], enabled: an && Boolean(nr), queryFn: () => R.aufnahmenZuordenbar(nr) });

/** Die offenen Angebote des Kunden einer Aufnahme — für „Einem Angebot zuordnen". */
export const useAngeboteFuerAufnahme = (id: string, an: boolean) =>
  useQuery({ queryKey: ["angeboteFuerAufnahme", id], enabled: an && Boolean(id), queryFn: () => R.angeboteFuerAufnahme(id) });

export function useAufnahmeZuordnen() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: ({ id, vorgang }: { id: string; vorgang: string }) => R.aufnahmeZuordnen(id, vorgang),
    onSuccess: (_z, e) => zuordnungFrisch(k, e.id),
  });
}

export function useAufnahmeLoesen() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => R.aufnahmeLoesen(id),
    onSuccess: (_a, id) => zuordnungFrisch(k, id),
  });
}

/**
 * **Angebot daraus erstellen — auch vor dem Aufmaß** (Paket aufnahmen2). Ist
 * sie gemessen, legt `POST /aufnahmen/:id/angebot` das Angebot mit der Zahl
 * an (0155). Ist sie es nicht, entsteht das Angebot auf dem gewöhnlichen Weg
 * — Kunde und Ort aus der Aufnahme, die Leistung als Zeile, die auf ihr
 * Aufmaß wartet („Menge folgt aus dem Aufmaß") — und die Aufnahme wird ihm
 * zugeordnet. Kein eigener Preisweg: Die Zeile kommt über `POST …/positionen`
 * wie jede andere.
 */
export function useAngebotAusOffenerAufnahme() {
  const k = useQueryClient();
  return useMutation({
    mutationFn: async (e: {
      id: string; kundeNr: string; ort: string; leistungsartId: string; betreff?: string;
    }): Promise<string> => {
      const neu = await R.angebotAnlegen({
        kunde: e.kundeNr, ...(e.ort ? { ort: e.ort } : {}), ...(e.betreff ? { betreff: e.betreff } : {}),
      });
      await R.positionAnlegen(neu.nr, { art: "leistung", leistungsart_id: e.leistungsartId });
      await R.aufnahmeZuordnen(e.id, neu.nr);
      return neu.nr;
    },
    onSuccess: (_nr, e) => zuordnungFrisch(k, e.id),
  });
}
